import { useEffect, useState } from 'react';
import { api, ApiError, type Issue } from '../api';
import { EMPTY_FORM, SaleForm, type SaleFormValues } from '../components/SaleForm';
import { Footer, TopBar } from '../components/TopBar';
import { revealLabel } from '../format';
import type { Recurrence } from '../../shared/recurrence';

interface ManageData {
  public_id: string;
  status: string;
  event_type: string;
  title: string;
  description: string;
  venue_name: string | null;
  address_1: string;
  address_2: string | null;
  city: string;
  state: string;
  postal_code: string;
  external_url: string | null;
  special_instructions: string | null;
  is_online_only: boolean;
  start_date: string;
  start_time: string;
  end_date: string;
  end_time: string;
  timezone: string;
  recurrence: Recurrence | null;
  until_date: string | null;
  address_reveal_at: string | null;
  reveal_date: string | null;
  reveal_time: string | null;
  // auction-only, already in the form's local date/time strings ('' when unset)
  auction_company: string;
  auction_format: string;
  auction_start_date: string;
  auction_start_time: string;
  preview_date: string;
  preview_start_time: string;
  preview_end_time: string;
  terms_url: string;
  catalog_url: string;
  pickup_info: string;
  contact: { contact_name: string; contact_phone: string; contact_email: string } | null;
}

/** Form fields for an existing repeat rule. */
function ruleFields(r: Recurrence | null): Partial<SaleFormValues> {
  if (!r) return {};
  if (r.freq === 'weekly') return { freq: r.interval === 1 ? 'weekly' : `every${r.interval}`, weekdays: r.weekdays.join(',') };
  if (r.freq === 'monthly_nth') return { freq: 'monthly_nth', nth: String(r.nth), weekday: String(r.weekday) };
  return { freq: 'monthly_day', monthday: String(r.day) };
}

export function Manage({ token }: { token: string }) {
  const [data, setData] = useState<ManageData | null>(null);
  const [missing, setMissing] = useState(false);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelled, setCancelled] = useState(false);

  useEffect(() => {
    document.title = 'Manage your listing · The Yard Sale Map';
    api
      .get<ManageData>(`/manage/${token}`)
      .then((d) => {
        setData(d);
        setCancelled(d.status === 'cancelled');
      })
      .catch(() => setMissing(true));
  }, [token]);

  if (missing) {
    return (
      <>
        <TopBar />
        <main className="page">
          <h1>That link isn't valid</h1>
          <p>Check the email we sent when you listed your sale — the edit link is in there.</p>
        </main>
      </>
    );
  }
  if (!data) {
    return (
      <>
        <TopBar />
        <main className="page" aria-live="polite">
          <p>Loading…</p>
        </main>
      </>
    );
  }

  const noTimes = data.start_time === '00:00' && data.end_time === '00:00';
  const initial: SaleFormValues = {
    ...EMPTY_FORM,
    event_type: data.event_type,
    title: data.title,
    description: data.description,
    venue_name: data.venue_name ?? '',
    address_1: data.address_1,
    address_2: data.address_2 ?? '',
    city: data.city,
    state: data.state,
    postal_code: data.postal_code,
    external_url: data.external_url ?? '',
    special_instructions: data.special_instructions ?? '',
    is_online_only: data.is_online_only,
    start_date: data.start_date,
    start_time: noTimes ? '07:00' : data.start_time,
    end_date: noTimes ? data.start_date : data.end_date,
    end_time: noTimes ? '13:00' : data.end_time,
    repeat: Boolean(data.recurrence),
    ...ruleFields(data.recurrence),
    until_date: data.until_date ?? '',
    // keep an existing reveal time exactly as set
    hide_address: Boolean(data.address_reveal_at),
    reveal_mode: data.address_reveal_at ? 'custom' : 'evening_before',
    reveal_date: data.reveal_date ?? '',
    reveal_time: data.reveal_time ?? '',
    auction_company: data.auction_company,
    auction_format: data.auction_format,
    auction_start_date: data.auction_start_date,
    auction_start_time: data.auction_start_time,
    preview_date: data.preview_date,
    preview_start_time: data.preview_start_time,
    preview_end_time: data.preview_end_time,
    terms_url: data.terms_url,
    catalog_url: data.catalog_url,
    pickup_info: data.pickup_info,
  };

  async function revealNow() {
    setBusy(true);
    try {
      await api.postJson(`/manage/${token}/reveal`);
      setData((d) => (d ? { ...d, address_reveal_at: null, reveal_date: null, reveal_time: null } : d));
      setMsg('Your address is now showing on the map.');
    } catch {
      setMsg("Couldn't update that. Try again.");
    } finally {
      setBusy(false);
    }
  }

  async function save(fields: Record<string, string>) {
    setBusy(true);
    setIssues([]);
    setMsg('');
    try {
      const r = await api.putJson<{ status: string }>(`/manage/${token}`, fields);
      setMsg(
        r.status === 'flagged'
          ? "Saved. We're giving the changes a quick look before they appear on the map."
          : 'Saved — your listing is updated.',
      );
      window.scrollTo(0, 0);
    } catch (e) {
      if (e instanceof ApiError) {
        setIssues(e.issues);
        setMsg(e.message);
      } else setMsg('Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function cancel() {
    setBusy(true);
    try {
      await api.postJson(`/manage/${token}/cancel`);
      setCancelled(true);
      window.scrollTo(0, 0);
    } catch {
      setMsg("Couldn't cancel — please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <TopBar />
      <main className="page" id="main">
        <h1>Manage your listing</h1>
        {cancelled ? (
          <p className="info">This sale has been cancelled and removed from the map.</p>
        ) : (
          <>
            <p>
              <a href={`/e/${data.public_id}`}>View the public listing</a>
            </p>
            {msg && (
              <p className={issues.length ? 'error' : 'info'} role="status">
                {msg}
              </p>
            )}
            {data.contact && (
              <p className="hint">
                Private contact on file: {data.contact.contact_name}, {data.contact.contact_phone}, {data.contact.contact_email}
              </p>
            )}
            {data.address_reveal_at && (
              <div className="info" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ flex: 1, minWidth: 200 }}>
                  Your address is hidden until {revealLabel(data.address_reveal_at, data.timezone)}. Shoppers see an approximate area.
                </span>
                <button type="button" className="primary" disabled={busy} onClick={() => void revealNow()}>
                  Show address now
                </button>
              </div>
            )}
            <SaleForm key={data.address_reveal_at ?? 'public'} mode="edit" initial={initial} initialNoTimes={noTimes} issues={issues} busy={busy} onSubmit={(f) => void save(f)} />
            <div className="form-section">
              <h2>Cancel this sale</h2>
              {!confirmCancel ? (
                <button type="button" className="danger" onClick={() => setConfirmCancel(true)}>
                  Cancel this sale
                </button>
              ) : (
                <div className="search-row">
                  <button type="button" className="danger" disabled={busy} onClick={() => void cancel()}>
                    Yes, remove it from the map
                  </button>
                  <button type="button" onClick={() => setConfirmCancel(false)}>
                    Keep it
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </main>
      <Footer />
    </>
  );
}
